import { Box, Grid, Typography, Button, Chip, Divider } from "@mui/material";
import { Email as EmailIcon, ArrowForward as ArrowIcon } from "@mui/icons-material";
import { volunteerRoles } from "../data/data.js";

const SDG_COLORS = ["#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2","#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E","#3F7E44","#0A97D9","#56C02B","#00689D","#19486A"];

export default function GetInvolved() {
  return (
    <Box
      id="volunteer"
      sx={{ bgcolor: "#F7F4EF", position: "relative", overflow: "hidden" }}
    >
      {/* ── Grain texture ── */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.5,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
        backgroundSize: "128px",
      }} />

      {/* Ghost decorative text */}
      <Box sx={{
        position: "absolute", right: -40, top: "50%",
        transform: "translateY(-50%) rotate(90deg)",
        fontFamily: "'Playfair Display', serif", fontSize: "200px",
        fontWeight: 900, color: "rgba(26,10,0,0.03)",
        pointerEvents: "none", whiteSpace: "nowrap", userSelect: "none", zIndex: 0,
      }}>
        JOIN US
      </Box>

      {/* ── Top label bar ── */}
      <Box sx={{
        borderBottom: "1px solid rgba(26,10,0,0.12)",
        px: { xs: 3, md: 6 }, py: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "relative", zIndex: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: "#5C3311",
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%,100%": { opacity: 1, transform: "scale(1)" },
              "50%":      { opacity: 0.4, transform: "scale(0.8)" },
            },
          }} />
          <Typography sx={{
            fontFamily: "'Space Mono', monospace", fontSize: "10px",
            letterSpacing: "3px", textTransform: "uppercase",
            color: "rgba(26,10,0,0.45)",
          }}>
            Be Part of It
          </Typography>
        </Box>
        <Typography sx={{
          fontFamily: "'Space Mono', monospace", fontSize: "10px",
          letterSpacing: "2px", color: "rgba(26,10,0,0.30)",
        }}>
          {String(volunteerRoles.length).padStart(2, "0")} Open Roles
        </Typography>
      </Box>

      {/* ── Section header ── */}
      <Box sx={{
        px: { xs: 3, md: 6 }, pt: { xs: 5, md: 6 }, pb: { xs: 4, md: 5 },
        display: "flex", flexDirection: { xs: "column", md: "row" },
        alignItems: { md: "flex-end" }, justifyContent: "space-between", gap: 3,
        position: "relative", zIndex: 1,
        borderBottom: "1px solid rgba(26,10,0,0.10)",
      }}>
        <Box>
          <Typography sx={{
            fontFamily: "'Space Mono', monospace", fontSize: "10px",
            letterSpacing: "3px", textTransform: "uppercase",
            color: "#5C3311", mb: 2, fontWeight: 700,
          }}>
            → Get Involved
          </Typography>
          <Typography sx={{
            fontFamily: "'Playfair Display', serif",
            fontSize: { xs: "36px", md: "52px", lg: "60px" },
            fontWeight: 700, color: "#1a0a00",
            lineHeight: 1.05, letterSpacing: "-1.5px",
          }}>
            Help build
            <br />
            <Box component="span" sx={{
              position: "relative",
              "&::after": {
                content: '""', position: "absolute",
                bottom: 6, left: 0, width: "100%", height: "3px",
                background: "linear-gradient(90deg,#E5243B,#DDA63A,#4C9F38)",
                borderRadius: "2px",
              },
            }}>
              Africa's future.
            </Box>
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 320 }}>
          <Typography sx={{
            fontSize: "14px", color: "rgba(26,10,0,0.52)",
            lineHeight: 1.78, mb: 3,
          }}>
            We are growing fast and need passionate Africans who believe in
            the mission. Join our volunteer team today.
          </Typography>
          <Box sx={{ display: "flex", height: "3px", borderRadius: "2px", overflow: "hidden" }}>
            {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
          </Box>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{
        px: { xs: 3, md: 6 },
        pt: { xs: 5, md: 6 },
        pb: { xs: 6, md: 8 },
        position: "relative", zIndex: 1,
      }}>
        <Grid container spacing={{ xs: 3, md: 4 }}>

          {/* ── Role cards — 3-col grid, cards stretch to fill ── */}
          <Grid size={12}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
                gap: { xs: 2, md: 3 },
              }}
            >
              {volunteerRoles.map((r, idx) => (
                <Box
                  key={r.name}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(26,10,0,0.07)",
                    borderRadius: "16px",
                    p: { xs: 3, md: 4 },
                    boxShadow: "0 1px 4px rgba(26,10,0,0.04)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      borderColor: "rgba(92,51,17,0.25)",
                      boxShadow: "0 8px 32px rgba(26,10,0,0.10)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  {/* ── Icon box ── */}
                  <Box sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    bgcolor: "#f0e8df",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#7a4520",
                    mb: 2.5,
                    "& svg": { fontSize: "22px" },
                  }}>
                    {r.icon}
                  </Box>

                  {/* ── Title + desc ── */}
                  <Typography sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#1a0a00",
                    lineHeight: 1.3,
                    mb: 1,
                  }}>
                    {r.name}
                  </Typography>
                  <Typography sx={{
                    fontSize: "13px",
                    color: "rgba(26,10,0,0.48)",
                    lineHeight: 1.65,
                    flex: 1,
                  }}>
                    {r.desc}
                  </Typography>

                  {/* ── Number ── */}
                  <Typography sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "rgba(26,10,0,0.14)",
                    letterSpacing: "0.5px",
                    alignSelf: "flex-end",
                    mt: 3,
                  }}>
                    {String(idx + 1).padStart(2, "0")}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* ── CTA panel — full width below cards ── */}
          <Grid size={12}>
            <Box sx={{
              bgcolor: "#fff",
              border: "1px solid rgba(26,10,0,0.10)",
              borderRadius: "16px",
              p: { xs: 3, md: 5 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 4, md: 8 },
              alignItems: { md: "center" },
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(26,10,0,0.04)",
            }}>
              {/* SDG gradient top bar */}
              <Box sx={{
                position: "absolute", top: 0, left: 0, right: 0, height: "4px",
                background: "linear-gradient(90deg,#E5243B,#DDA63A,#4C9F38,#26BDE2,#FCC30B,#A21942)",
              }} />

              {/* ── Left: heading + copy ── */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: { xs: "24px", md: "30px" },
                  fontWeight: 700, color: "#1a0a00",
                  lineHeight: 1.2, mb: 1.5, mt: 1,
                }}>
                  Ready to Build Africa's Future?
                </Typography>
                <Box sx={{
                  width: "40px", height: "2px",
                  bgcolor: "#DDA63A", borderRadius: "1px", mb: 2,
                }} />
                <Typography sx={{
                  color: "rgba(26,10,0,0.55)", fontSize: "15px",
                  lineHeight: 1.78,
                }}>
                  Send your CV and a motivation note to our team. Share your
                  application on social media to amplify the movement!
                </Typography>
              </Box>

              {/* ── Divider (vertical on md+) ── */}
              <Box sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                alignSelf: "stretch",
                bgcolor: "rgba(26,10,0,0.08)",
                flexShrink: 0,
              }} />

              {/* ── Right: contact + actions ── */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Email row */}
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  bgcolor: "rgba(92,51,17,0.05)",
                  border: "1px solid rgba(92,51,17,0.15)",
                  borderRadius: "10px", px: 2.5, py: 2,
                }}>
                  <EmailIcon sx={{ color: "#5C3311", flexShrink: 0, fontSize: "20px" }} />
                  <Typography sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "13px", color: "#5C3311", wordBreak: "break-all",
                  }}>
                    africabuildtoshelter@gmail.com
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "10px", color: "rgba(26,10,0,0.40)",
                    letterSpacing: "1.5px", textTransform: "uppercase", mb: 1,
                  }}>
                    Tag us on social media:
                  </Typography>
                  <Chip
                    label="#BuildToShelterAfrica"
                    sx={{
                      bgcolor: "rgba(221,166,58,0.15)",
                      color: "#5C3311",
                      border: "1px solid rgba(221,166,58,0.50)",
                      fontFamily: "'Space Mono', monospace",
                      fontWeight: 700, fontSize: "12px",
                      height: 36,
                    }}
                  />
                </Box>

                <Divider sx={{ borderColor: "rgba(26,10,0,0.08)" }} />

                <Box sx={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap", gap: 2,
                }}>
                  <Typography sx={{
                    color: "rgba(26,10,0,0.45)", fontSize: "13px",
                    lineHeight: 1.72, maxWidth: 260,
                  }}>
                    We also welcome partnerships from organizations, corporates,
                    and institutions aligned with our mission.
                  </Typography>
                  <Button
                    variant="contained"
                    href="#contact"
                    endIcon={<ArrowIcon />}
                    sx={{
                      bgcolor: "#5C3311", color: "#fff",
                      fontWeight: 700, fontSize: "14px",
                      letterSpacing: "0.3px",
                      px: 3, py: 1.5,
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(92,51,17,0.25)",
                      whiteSpace: "nowrap",
                      "&:hover": { bgcolor: "#7a4520" },
                    }}
                  >
                    Partner With Us
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Box>

      {/* ── Bottom bar ── */}
      <Box sx={{
        borderTop: "1px solid rgba(26,10,0,0.10)",
        px: { xs: 3, md: 6 }, py: 2.5,
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap", gap: 2,
        position: "relative", zIndex: 1,
      }}>
        <Typography sx={{
          fontFamily: "'Space Mono', monospace", fontSize: "10px",
          color: "rgba(26,10,0,0.35)", letterSpacing: "2px",
          textTransform: "uppercase",
        }}>
          Youth-led · Pan-African · Est. 2025
        </Typography>
        <Typography
          component="a" href="#contact"
          sx={{
            fontFamily: "'Space Mono', monospace", fontSize: "11px",
            fontWeight: 700, color: "#5C3311", textDecoration: "none",
            letterSpacing: "1.5px", textTransform: "uppercase",
            borderBottom: "1.5px solid #5C3311", pb: "1px",
            transition: "all 0.2s",
            "&:hover": { color: "#DDA63A", borderColor: "#DDA63A" },
          }}
        >
          Contact Us →
        </Typography>
      </Box>
    </Box>
  );
}
